import { sub } from 'date-fns';
import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { VantageNames } from '@/vantage/constants';
import { type ActionsFactory, type StoreInstance, createStore, createStoreInstance, deepMerge } from '@seeqdev/utils';
import type { ViewDateRange } from '@/vantage/types/DateTime.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { GridState } from '@seeqdev/ag/grid';

/** Default filters for the grid */
const defaultFilters = {
  [VantageNames.DerivedColumns.Extinct]: { filterType: 'set', values: ['False'] },
};

export interface VantageTableState {
  /** Rows currently selected in the evidence table. Not persisted. */
  selection: SimplifiedNode[];
  /** AG Grid native state (filters, grouping, column visibility, ...), always carrying the default filters. */
  gridState: GridState;
  /** Free-text filter applied across all evidence table columns. */
  quickFilter: string;
  /**
   * Legacy relative/fixed date range. The workstep upgrader moves it into the duration store, but it is still
   * persisted and read by Save View tracking.
   */
  dateRange: ViewDateRange;
}

export type VantageTableDehydratedState = Omit<VantageTableState, 'selection'>;

export interface VantageTableActions {
  /** Replaces the rows selected in the evidence table with a copy, so the caller's nodes are not frozen. */
  setSelection: (selection: SimplifiedNode[]) => void;
  /** Stores the grid state, adding back any default filter the grid's filter model does not set. */
  setGridState: (gridState: GridState) => void;
  /** Shows or hides a column in the current grid state. */
  setColumnVisible: (colId: string, visible: boolean) => void;
  /** Sets the free-text filter applied across all evidence table columns. */
  setQuickFilter: (quickFilter: string) => void;
  /** Restores the defaults, with a default date range that ends now. */
  reset: (overrides?: Partial<VantageTableState>) => void;
}

const buildDefaultDateRange = (): ViewDateRange => ({
  lookBack: { num: 7, unit: { value: 'day', label: 'day' } },
  lookForward: { num: 0, unit: { value: 'day', label: 'day' } },
  range: [sub(new Date(), { hours: 7 * 24 }), new Date()],
});

const buildInitialState = (): VantageTableState => ({
  selection: [],
  // Copied so rehydrate's in-place merge cannot write into the shared defaults.
  gridState: { filter: { filterModel: structuredClone(defaultFilters) } },
  quickFilter: '',
  dateRange: buildDefaultDateRange(),
});

const initialState: VantageTableState = Object.freeze(buildInitialState());

const vantageTableActionsFactory: ActionsFactory<VantageTableState, VantageTableActions> = (set, _get, resetState) => ({
  setSelection: (selection) =>
    set((draft) => {
      draft.selection = structuredClone(selection);
    }),

  setGridState: (gridState) =>
    set((draft) => {
      const filterModel = { ...defaultFilters, ...gridState.filter?.filterModel };
      draft.gridState = { ...gridState, filter: { ...gridState.filter, filterModel } };
    }),

  setColumnVisible: (colId, visible) =>
    set((draft) => {
      const hiddenColIds = new Set(draft.gridState.columnVisibility?.hiddenColIds ?? []);
      if (visible) {
        hiddenColIds.delete(colId);
      } else {
        hiddenColIds.add(colId);
      }
      draft.gridState.columnVisibility = { hiddenColIds: Array.from(hiddenColIds) };
    }),

  setQuickFilter: (quickFilter) =>
    set((draft) => {
      draft.quickFilter = quickFilter;
    }),

  reset: (overrides) => resetState({ dateRange: buildDefaultDateRange(), ...overrides }),
});

export const dehydrateVantageTableState = (state: VantageTableState): VantageTableDehydratedState => ({
  gridState: state.gridState,
  quickFilter: state.quickFilter,
  dateRange: state.dateRange,
});

/**
 * Replaces the store state with the defaults deep-merged with the persisted state, clearing the selection. The deep
 * merge matters for worksteps saved before the default filters were written on every grid change: their grid state
 * still picks up the default Extinct filter.
 */
export const rehydrateVantageTableState = (
  store: Pick<VantageTableStore, 'setState'>,
  { gridState, quickFilter, dateRange }: Partial<VantageTableDehydratedState>,
) => {
  store.setState(deepMerge(buildInitialState(), { gridState, quickFilter, dateRange }), true);
};

const STORE_NAME = 'VantageTable';

const [VantageTableStoreProvider, useVantageTableStoreInstance, useVantageTableSelector] = createStore(
  STORE_NAME,
  initialState,
  vantageTableActionsFactory,
);

export { VantageTableStoreProvider };

export const useVantageTableSelection = () => useVantageTableSelector((state) => state.selection);
export const useVantageTableGridState = () => useVantageTableSelector((state) => state.gridState);
export const useVantageTableQuickFilter = () => useVantageTableSelector((state) => state.quickFilter);
export const useVantageTableDateRange = () => useVantageTableSelector((state) => state.dateRange);

export const useVantageTableActions = () => useVantageTableStoreInstance().actions;

export type VantageTableStore = StoreInstance<VantageTableState, VantageTableActions>;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateVantageTableStore(initialStateOverrides?: Partial<VantageTableState>): VantageTableStore {
  return createStoreInstance<VantageTableState, VantageTableActions>(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    vantageTableActionsFactory,
    { storeName: STORE_NAME },
  );
}

/**
 * Registers the VantageTable store with the persistence system.
 * Uses the legacy store name 'sqVantageTableStore' for workstep compatibility.
 */
export const usePersistVantageTableStore = () => {
  const register = useRegisterPersistedStore();
  const store = useVantageTableStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqVantageTableStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateVantageTableState,
      rehydrate: (dehydratedState: Partial<VantageTableDehydratedState>) =>
        rehydrateVantageTableState(store, dehydratedState),
    });
  }, [register, store]);
};
